import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from '../../../utils/classNames';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { formatLocationDisplay } from '../../../utils/location';
import {
  buildBandStyleTags,
  normalizeStyleTagList,
} from '../../../utils/styleTags';
import {
  buildMediaTrackRows,
} from '../../../utils/profileEditorial';
import { getActiveBandSeekingDetails } from '../../../utils/bandSeeking';
import { isActiveBandMembershipStatus } from '../../../utils/bandMembership';
import EditingModeBanner from '../../molecules/EditingModeBanner';
import EditorialIconButton from '../../atoms/EditorialIconButton';
import { PencilIcon } from '../../atoms/icons/EditorialProfileIcons';
import LocationModal from '../../molecules/LocationModal';
import ProfilePicturePreviewModal from '../../molecules/ProfilePicturePreviewModal';
import PublicAuthAction from '../../molecules/PublicAuthAction';
import SectionTabbar from '../../molecules/SectionTabbar';
import BackButton from '../../atoms/BackButton';
import { MobileHeaderSlotContext } from '../../organisms/MainPage/MobileNavigation/MobileHeaderSlotContext';
import BandHero from './sections/BandHero';
import BandSeekingSection from './sections/BandSeekingSection';
import BandMembersSection from './sections/BandMembersSection';
import BandScheduleSection from './sections/BandScheduleSection';
import BandPostsSection from './sections/BandPostsSection';
import BandGallerySection from './sections/BandGallerySection';
import BandMediaSection from './sections/BandMediaSection';
import BandFollowersSection from './sections/BandFollowersSection';
import BandCtaSection from './sections/BandCtaSection';
import adaptBandToPageData from './adaptBand';
import './index.scss';

const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

const BAND_PAGE_SECTION_IDS = Object.freeze({
  OVERVIEW: 'overview',
  SEEKING: 'seeking',
  MEMBERS: 'members',
  FOLLOWERS: 'followers',
  SCHEDULE: 'schedule',
  POSTS: 'posts',
  GALLERY: 'gallery',
  MEDIA: 'media',
});

const BAND_PAGE_TAB_SPECS = Object.freeze([
  { id: BAND_PAGE_SECTION_IDS.OVERVIEW, labelKey: 'bandPage.tabs.overview', defaultLabel: 'Overview' },
  { id: BAND_PAGE_SECTION_IDS.SEEKING, labelKey: 'bandPage.tabs.seeking', defaultLabel: 'Seeking' },
  { id: BAND_PAGE_SECTION_IDS.MEMBERS, labelKey: 'bandPage.tabs.members', defaultLabel: 'Lineup' },
  { id: BAND_PAGE_SECTION_IDS.SCHEDULE, labelKey: 'bandPage.tabs.schedule', defaultLabel: 'Schedule' },
  { id: BAND_PAGE_SECTION_IDS.POSTS, labelKey: 'bandPage.tabs.posts', defaultLabel: 'Posts' },
  { id: BAND_PAGE_SECTION_IDS.GALLERY, labelKey: 'bandPage.tabs.gallery', defaultLabel: 'Gallery' },
  { id: BAND_PAGE_SECTION_IDS.MEDIA, labelKey: 'bandPage.tabs.media', defaultLabel: 'Media' },
  { id: BAND_PAGE_SECTION_IDS.FOLLOWERS, labelKey: 'bandPage.tabs.followers', defaultLabel: 'Followers' },
]);

const setPixelVar = (element, name, value) => {
  if (!element || !Number.isFinite(value)) return;
  element.style.setProperty(name, `${Math.ceil(value)}px`);
};

const buildEditableStyleTags = (band) => {
  const canonicalTags = [
    ...(Array.isArray(band?.genres) ? band.genres : []),
    ...(Array.isArray(band?.styles) ? band.styles : []),
  ];
  if (canonicalTags.length > 0) {
    return normalizeStyleTagList(canonicalTags);
  }
  return buildBandStyleTags(band);
};

const buildEditableLocationDisplay = (band) => {
  if (typeof band?.locationDisplay === 'string' && band.locationDisplay.trim()) {
    return band.locationDisplay.trim();
  }
  if (typeof band?.location === 'string' && band.location.trim()) {
    return band.location.trim();
  }
  if (band?.location && typeof band.location === 'object') {
    return formatLocationDisplay(band.location);
  }
  return formatLocationDisplay({
    city: band?.city,
    stateOrRegion: band?.stateOrRegion,
    country: band?.country,
  });
};

const normalizeLocationCoordinate = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const normalizeBandVisibility = (value) => {
  const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return normalized === 'PRIVATE' ? 'PRIVATE' : 'PUBLIC';
};

const buildBandLocationModalInitialLocation = (band) => {
  if (!band) {
    return null;
  }

  const structured = band.location && typeof band.location === 'object' ? band.location : {};
  return {
    country: structured.country ?? band.country,
    countryCode: structured.countryCode ?? band.countryCode,
    stateOrRegion: structured.stateOrRegion ?? band.stateOrRegion,
    city: structured.city ?? band.city,
    latitude: normalizeLocationCoordinate(
      structured.latitude ?? structured.coordinateLat ?? band.latitude ?? band.coordinateLat,
    ),
    longitude: normalizeLocationCoordinate(
      structured.longitude ?? structured.coordinateLng ?? band.longitude ?? band.coordinateLng,
    ),
    formatted: structured.formatted
      ?? (typeof band.location === 'string' ? band.location : undefined)
      ?? band.locationDisplay,
    source: structured.source ?? band.locationSource,
  };
};

const stripFileExtension = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/\.[^/.]+$/, '').trim();
};

const resolveGalleryPhotoImageUrl = (photo) => {
  const rawUrl = photo?.imageUrl || photo?.url || photo?.displayUrl || photo?.previewUrl || '';
  return rawUrl ? resolveMediaUrl(rawUrl) : null;
};

const getGalleryPhotoId = (photo, imageUrl, fallback) => (
  photo?.id
  || photo?.objectKey
  || photo?.url
  || photo?.imageUrl
  || imageUrl
  || fallback
);

const buildMediaGalleryPhotos = (photos, bandName) => (
  (Array.isArray(photos) ? photos : [])
    .map((photo, index) => {
      const imageUrl = resolveGalleryPhotoImageUrl(photo);
      if (!imageUrl) {
        return null;
      }
      const caption = photo?.caption || stripFileExtension(photo?.originalFileName || '');
      return {
        ...photo,
        id: getGalleryPhotoId(photo, imageUrl, `band-gallery-photo-${index}`),
        imageUrl,
        url: imageUrl,
        thumb: photo?.thumb || imageUrl,
        caption,
        alt: photo?.alt || caption || `Gallery photo from ${bandName || 'band'}`,
        source: photo?.source || 'media',
      };
    })
    .filter(Boolean)
);

const mergeGalleryPhotos = (basePhotos, additionalPhotos) => {
  const merged = Array.isArray(basePhotos) ? [...basePhotos] : [];
  const seenUrls = new Set(
    merged
      .map((photo) => photo?.imageUrl || photo?.url)
      .filter(Boolean),
  );
  additionalPhotos.forEach((photo) => {
    if (!photo?.imageUrl || seenUrls.has(photo.imageUrl)) {
      return;
    }
    seenUrls.add(photo.imageUrl);
    merged.push(photo);
  });
  return merged;
};

/**
 * BandPage — full editorial band profile, rendered in place of the catalog
 * when a user opens a `band-catalog-card`. Sections gracefully omit when
 * the underlying data isn't present, so the page degrades for bands that
 * only have core fields wired (and renders fully when backend coverage
 * lands or curated `editorial` data is supplied).
 *
 * Edit mode (mirrors the Home page Edit Profile flow): when `canEdit` is
 * true the owner sees Edit toggles in the back-bar and hero. Toggling on
 * applies `band-page--editing` to the root, surfaces an EditingModeBanner,
 * and swaps editable fields to controlled edit controls. Saves are
 * delegated to the parent via `onSaveField(field, value)` or
 * `onSaveLocation(payload)` for the shared location modal.
 */
export default function BandPage({
  band,
  onBack,
  // onOpenMyBand,
  backButtonPlacement = 'bar',
  onFollow,
  onLeaveBand,
  onTickets,
  onShare,
  onPublicView,
  onVisibilityChange,
  isVisibilityUpdating = false,
  onMemberClick,
  onFollowerClick,
  onPhotoClick,
  onEditCover,
  onEditAvatar,
  onUploadTrack,
  onUploadPhoto,
  onDeleteTrack,
  onDeletePhoto,
  onMoveTrack,
  onReorderPhotos,
  onPlayTrack,
  onSeekTrack,
  onStartPost,
  onCreateEvent,
  onEditEvent,
  onDeleteEvent,
  onSubscribe,
  canEdit = false,
  currentUserId = null,
  coverImageUrl,
  avatarImageUrl,
  posts,
  tracks: providedTracks,
  playsCount = null,
  playingTrackId = null,
  trackProgressById = {},
  deletingTrackId = null,
  isTrackReordering = false,
  galleryPhotos: providedGalleryPhotos,
  followers: providedFollowers,
  followersLoaded = false,
  loadFollowersPage,
  deletingGalleryPhotoId = null,
  isGalleryPhotoReordering = false,
  postComposerProps,
  onSaveField,
  onSaveLocation,
  onSaveMemberInstrument,
  onRemoveMember,
  removingMemberUserId = null,
  onInviteMember,
  isFollowing = false,
  isFollowLoading = false,
  isLeaveBandLoading = false,
  className = '',
}) {
  const { t, i18n = {} } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || 'en';
  // Non-null when the section tabbar is portaled into the mobile app header —
  // the back button becomes an icon-only "<" circle there (labelled pill on
  // desktop). Mirrors SectionTabbar's own portaling condition.
  const mobileHeaderSlot = useContext(MobileHeaderSlotContext);
  const rootRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState(BAND_PAGE_SECTION_IDS.OVERVIEW);
  const [showAvatarPreview, setShowAvatarPreview] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [locationModalInitialValues, setLocationModalInitialValues] = useState(null);

  const data = useMemo(() => adaptBandToPageData(band, t, { playsCount }), [band, playsCount, t]);
  const bandVisibility = normalizeBandVisibility(band?.visibility);
  const seekingDetails = useMemo(() => getActiveBandSeekingDetails({
    seekingMembers: data?.seekingMembers,
    instrumentsNeeded: data?.instrumentsNeeded,
  }), [data?.instrumentsNeeded, data?.seekingMembers]);
  const normalizedCurrentUserId = currentUserId == null ? null : String(currentUserId);
  const publicMedia = !normalizedCurrentUserId;
  const pagePosts = useMemo(() => (
    Array.isArray(posts) ? posts : (data?.posts || [])
  ), [data?.posts, posts]);
  const galleryPhotos = useMemo(() => {
    const uploadedPhotos = buildMediaGalleryPhotos(providedGalleryPhotos, data?.name || band?.name);
    return mergeGalleryPhotos(data?.gallery, uploadedPhotos);
  }, [band?.name, data?.gallery, data?.name, providedGalleryPhotos]);
  const mediaTracks = useMemo(() => buildMediaTrackRows(providedTracks, {
    locale,
    playingTrackId,
    t,
    trackProgressById,
    untitledTrackKey: 'bandPage.media.untitledTrack',
    playsCountKey: 'bandPage.media.playsCount',
  }), [locale, playingTrackId, providedTracks, t, trackProgressById]);
  const editableValues = useMemo(() => ({
    name: typeof band?.name === 'string' ? band.name : '',
    description: typeof band?.description === 'string' ? band.description : '',
    locationDisplay: buildEditableLocationDisplay(band),
    foundedYear: data?.foundedYear || '',
    styleTags: buildEditableStyleTags(band),
  }), [band, data?.foundedYear]);

  const handleSaveField = useCallback((field, value) => {
    if (typeof onSaveField !== 'function') return Promise.resolve();
    return Promise.resolve(onSaveField(field, value));
  }, [onSaveField]);

  const handleOpenLocationModal = useCallback(() => {
    setLocationModalInitialValues({
      display: editableValues.locationDisplay,
      location: buildBandLocationModalInitialLocation(band),
    });
    setIsLocationModalOpen(true);
  }, [band, editableValues.locationDisplay]);

  const handleCloseLocationModal = useCallback(() => {
    setIsLocationModalOpen(false);
    setLocationModalInitialValues(null);
  }, []);

  const handleSaveLocation = useCallback((payload) => {
    if (typeof onSaveLocation === 'function') {
      return Promise.resolve(onSaveLocation(payload));
    }
    if (typeof onSaveField === 'function') {
      return Promise.resolve(onSaveField('locationDisplay', payload?.locationDisplay || ''));
    }
    return Promise.resolve();
  }, [onSaveField, onSaveLocation]);

  const handleSaveMemberInstrument = useCallback((memberUserId, instrument) => {
    if (typeof onSaveMemberInstrument !== 'function') return Promise.resolve();
    return Promise.resolve(onSaveMemberInstrument(memberUserId, instrument));
  }, [onSaveMemberInstrument]);

  const canEditOwnMemberInstrument = useMemo(() => (
    Boolean(
      normalizedCurrentUserId
        && data?.members?.some((member) => String(member.userId) === normalizedCurrentUserId),
    )
  ), [data?.members, normalizedCurrentUserId]);
  const canEditMemberInstruments = Boolean(
    typeof onSaveMemberInstrument === 'function'
      && (canEdit || canEditOwnMemberInstrument),
  );
  const canEnterEditMode = Boolean(canEdit || canEditMemberInstruments);
  // const canOpenMyBand = typeof onOpenMyBand === 'function';
  const pageEditing = Boolean(canEnterEditMode && isEditing);
  const bandFieldEditing = Boolean(canEdit && isEditing);
  const shouldShowSeekingSection = Boolean(bandFieldEditing || seekingDetails);
  const shouldShowFollowersSection = Boolean(normalizedCurrentUserId);
  const toggleEditing = useCallback(() => setIsEditing((prev) => !prev), []);
  const exitEditing = useCallback(() => setIsEditing(false), []);
  const hasBackBar = Boolean(onBack && backButtonPlacement === 'bar');
  const hasTabbarBackButton = Boolean(onBack && backButtonPlacement === 'tabbar');
  const tabs = useMemo(() => (
    BAND_PAGE_TAB_SPECS
      .filter((tab) => {
        if (tab.id === BAND_PAGE_SECTION_IDS.SEEKING) {
          return shouldShowSeekingSection;
        }
        if (tab.id === BAND_PAGE_SECTION_IDS.MEMBERS) {
          return data?.members?.length > 0;
        }
        if (tab.id === BAND_PAGE_SECTION_IDS.FOLLOWERS) {
          return shouldShowFollowersSection;
        }
        return true;
      })
      .map((tab) => ({
        id: tab.id,
        label: t(tab.labelKey, { defaultValue: tab.defaultLabel }),
      }))
  ), [data?.members?.length, shouldShowFollowersSection, shouldShowSeekingSection, t]);

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeSection)) {
      setActiveSection(tabs[0]?.id || BAND_PAGE_SECTION_IDS.OVERVIEW);
    }
  }, [activeSection, tabs]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') {
      return undefined;
    }

    const backBar = root.querySelector('.band-page__back-bar');
    const tabbar = root.querySelector('.band-page__section-tabbar');
    const updateStickyOffsets = () => {
      const backBarHeight = backBar?.getBoundingClientRect?.().height || 0;
      const tabbarHeight = tabbar?.getBoundingClientRect?.().height || 0;
      setPixelVar(root, '--band-page-back-bar-height', backBarHeight);
      setPixelVar(root, '--band-page-tabbar-height', tabbarHeight);
    };

    updateStickyOffsets();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateStickyOffsets);
      if (backBar) observer.observe(backBar);
      if (tabbar) observer.observe(tabbar);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateStickyOffsets);
    return () => {
      window.removeEventListener('resize', updateStickyOffsets);
    };
  }, [canEnterEditMode, hasBackBar, tabs]);

  if (!data) return null;

  const effectiveAvatarUrl = avatarImageUrl || data.hero.avatarUrl || null;
  const canPreviewAvatar = Boolean(effectiveAvatarUrl);
  const openAvatarPreview = () => {
    if (canPreviewAvatar) {
      setShowAvatarPreview(true);
    }
  };
  const closeAvatarPreview = () => setShowAvatarPreview(false);

  const editToggleLabel = pageEditing
    ? t('bandPage.edit.exit', { defaultValue: 'Done' })
    : (canEdit
      ? t('bandPage.edit.enter', { defaultValue: 'Edit band' })
      : t('bandPage.edit.enterMembership', { defaultValue: 'Edit membership' }));
  const bannerMessage = canEdit
    ? t('bandPage.edit.bannerTitle', { defaultValue: 'Editing your band' })
    : t('bandPage.edit.memberInstrumentBannerTitle', { defaultValue: 'Editing your band role' });
  const bannerHint = canEdit
    ? t('bandPage.edit.bannerHint', { defaultValue: 'Click any field on the page to update it. Changes save automatically.' })
    : t('bandPage.edit.memberInstrumentBannerHint', { defaultValue: 'Choose the instrument you play in this band from your member card.' });
  const followLabel = isFollowLoading
    ? t('bandPage.hero.followUpdating', { defaultValue: 'Updating' })
    : (isFollowing
      ? t('bandPage.hero.unfollow', { defaultValue: 'Unfollow' })
      : t('bandPage.hero.follow', { defaultValue: 'Follow' }));
  const leaveBandLabel = isLeaveBandLoading
    ? t('bandPage.hero.leavingBand', { defaultValue: 'Leaving…' })
    : t('bandPage.hero.leaveBand', { defaultValue: 'Leave band' });
  const isCurrentUserActiveBandMember = Boolean(
    normalizedCurrentUserId
      && band?.currentUserIsMember === true
      && isActiveBandMembershipStatus(band?.currentUserMembershipStatus),
  );
  const membersHeaderSide = canEdit && typeof onInviteMember === 'function' ? (
    <span className="band-members__header-action">
      <button type="button" className="band-members__invite-button" onClick={onInviteMember}>
        {t('bandPage.members.inviteMember', { defaultValue: 'Invite member' })}
      </button>
    </span>
  ) : null;
  const tabbarActions = (() => {
    if (canEnterEditMode) {
      return (
        <>
          {/*
            Commented as this will be implemented in Release Milestone 2.
            {typeof onOpenMyBand === 'function' ? (
              <button
                type="button"
                className="band-page__my-band-action"
                onClick={onOpenMyBand}
              >
                {t('bandsWelcomePage.bandManagement', { defaultValue: 'Band managment' })}
              </button>
            ) : null}
          */}
          <EditorialIconButton
            icon={<PencilIcon width="18" height="18" />}
            label={editToggleLabel}
            active={pageEditing}
            onClick={toggleEditing}
          />
        </>
      );
    }
    if (!normalizedCurrentUserId) {
      return <PublicAuthAction />;
    }
    return null;
  })();

  return (
    <article ref={rootRef} className={classNames('band-page', hasBackBar && 'band-page--with-back-bar', pageEditing && 'band-page--editing', className)}>
      {hasBackBar ? (
        <div className="band-page__back-bar">
          <button type="button" className="band-page__back" onClick={onBack}>
            <ChevronLeftIcon />
            {t('bandPage.back', { defaultValue: 'Back to bands' })}
          </button>
        </div>
      ) : null}

      <SectionTabbar
        tabs={tabs}
        activeId={activeSection}
        onChange={setActiveSection}
        ariaLabel={t('bandPage.tabsAria', { defaultValue: 'Band sections' })}
        className="band-page__section-tabbar"
        centerTabs={hasTabbarBackButton}
        leading={hasTabbarBackButton ? (
          <BackButton
            label={t('bandPage.back', { defaultValue: 'Back to bands' })}
            onClick={onBack}
            iconOnly={Boolean(mobileHeaderSlot)}
            className="band-page__back--tabbar"
          />
        ) : null}
        showActionsOnMobile={Boolean(tabbarActions)}
        actions={tabbarActions}
      />

      {pageEditing ? (
        <EditingModeBanner
          message={bannerMessage}
          hint={bannerHint}
          doneLabel={t('bandPage.edit.exit', { defaultValue: 'Done' })}
          onDone={exitEditing}
        />
      ) : null}

      <BandHero
        id={BAND_PAGE_SECTION_IDS.OVERVIEW}
        bandName={data.hero.bandName}
        avatarUrl={effectiveAvatarUrl}
        avatarSeed={band?.id || band?.objectId || data.hero.bandName}
        monogram={data.monogram}
        coverImageUrl={coverImageUrl || data.hero.coverImageUrl}
        status={data.hero.status}
        facts={data.hero.facts}
        stats={data.hero.stats}
        editing={bandFieldEditing}
        isOwner={canEdit}
        editableName={editableValues.name}
        tagline={data.about.quote || data.about.title}
        editableDescription={editableValues.description}
        editableLocation={editableValues.locationDisplay}
        editableFoundedYear={editableValues.foundedYear}
        editableTags={editableValues.styleTags}
        onSaveField={handleSaveField}
        onEditLocation={bandFieldEditing ? handleOpenLocationModal : undefined}
        onEditToggle={toggleEditing}
        onCoverEdit={bandFieldEditing ? onEditCover : undefined}
        coverEditLabel={t('bandPage.hero.coverEdit', { defaultValue: 'Edit cover' })}
        onAvatarClick={canPreviewAvatar ? openAvatarPreview : undefined}
        onAvatarEdit={canEdit ? onEditAvatar : undefined}
        avatarPreviewLabel={t('bandPage.hero.avatarPreview', { defaultValue: 'View band avatar' })}
        avatarEditLabel={t('bandPage.hero.avatarEdit', { defaultValue: 'Edit' })}
        avatarEditAriaLabel={t('bandPage.hero.avatarEditAria', { defaultValue: 'Edit band avatar' })}
        onFollow={canEdit ? undefined : onFollow}
        followLabel={followLabel}
        followDisabled={isFollowLoading}
        onLeaveBand={isCurrentUserActiveBandMember ? onLeaveBand : undefined}
        leaveBandLabel={leaveBandLabel}
        leaveBandDisabled={isLeaveBandLoading}
        visibility={bandVisibility}
        onVisibilityChange={canEdit ? onVisibilityChange : undefined}
        visibilityDisabled={isVisibilityUpdating}
        onPublicView={canEdit ? onPublicView : undefined}
        publicLabel={t('bandPage.hero.publicView', { defaultValue: 'View as public' })}
        onTickets={onTickets}
        onShare={onShare}
      />

      {shouldShowSeekingSection ? (
        <BandSeekingSection
          id={BAND_PAGE_SECTION_IDS.SEEKING}
          seekingMembers={data.seekingMembers}
          instrumentsNeeded={data.instrumentsNeeded}
          editing={bandFieldEditing}
          onFieldSave={handleSaveField}
        />
      ) : null}

      <BandMembersSection
        id={BAND_PAGE_SECTION_IDS.MEMBERS}
        side={membersHeaderSide}
        members={data.members}
        onMemberClick={onMemberClick}
        editing={pageEditing}
        currentUserId={normalizedCurrentUserId}
        isBandOwner={canEdit}
        isCurrentUserMember={isCurrentUserActiveBandMember}
        onSaveMemberInstrument={handleSaveMemberInstrument}
        onRemoveMember={canEdit ? onRemoveMember : undefined}
        removingMemberUserId={removingMemberUserId}
        publicMedia={publicMedia}
      />
      <BandScheduleSection
        id={BAND_PAGE_SECTION_IDS.SCHEDULE}
        events={data.events}
        onCreateEvent={canEdit ? onCreateEvent : undefined}
        onEditEvent={canEdit ? onEditEvent : undefined}
        onDeleteEvent={canEdit ? onDeleteEvent : undefined}
        canManageEvents={canEdit}
      />
      <BandPostsSection
        id={BAND_PAGE_SECTION_IDS.POSTS}
        posts={pagePosts}
        currentUserId={normalizedCurrentUserId}
        readOnly={!normalizedCurrentUserId}
        onStartPost={canEdit ? onStartPost : undefined}
        postComposerProps={postComposerProps}
        publicMedia={publicMedia}
      />
      <BandGallerySection
        id={BAND_PAGE_SECTION_IDS.GALLERY}
        photos={galleryPhotos}
        isEditing={bandFieldEditing}
        onPhotoClick={onPhotoClick}
        onUploadPhoto={canEdit ? onUploadPhoto : undefined}
        onDeletePhoto={canEdit ? onDeletePhoto : undefined}
        onReorderPhotos={canEdit ? onReorderPhotos : undefined}
        deletingPhotoId={deletingGalleryPhotoId}
        isReorderingPhotos={isGalleryPhotoReordering}
      />
      <BandMediaSection
        id={BAND_PAGE_SECTION_IDS.MEDIA}
        tracks={mediaTracks}
        isEditing={bandFieldEditing}
        onUploadTrack={canEdit ? onUploadTrack : undefined}
        onPlayTrack={onPlayTrack}
        onSeekTrack={onSeekTrack}
        onDeleteTrack={canEdit ? onDeleteTrack : undefined}
        onMoveTrack={canEdit ? onMoveTrack : undefined}
        deletingTrackId={deletingTrackId}
        isReorderingTracks={isTrackReordering}
        uploadAudioLabel={t('bandPage.media.uploadTrack', { defaultValue: 'Upload audio' })}
      />
      {onSubscribe ? <BandCtaSection onSubmit={onSubscribe} /> : null}
      {shouldShowFollowersSection ? (
        <BandFollowersSection
          id={BAND_PAGE_SECTION_IDS.FOLLOWERS}
          followers={Array.isArray(providedFollowers) ? providedFollowers : []}
          loaded={followersLoaded}
          loadFollowersPage={loadFollowersPage}
          onFollowerClick={onFollowerClick}
          publicMedia={publicMedia}
        />
      ) : null}
      {showAvatarPreview && effectiveAvatarUrl ? (
        <ProfilePicturePreviewModal
          src={effectiveAvatarUrl}
          caption={data.hero.bandName}
          alt={t('bandPage.hero.avatarPreviewAlt', {
            name: data.hero.bandName,
            defaultValue: `${data.hero.bandName} avatar`,
          })}
          onClose={closeAvatarPreview}
        />
      ) : null}
      {isLocationModalOpen ? (
        <LocationModal
          initialLocation={locationModalInitialValues?.location || null}
          initialDisplay={locationModalInitialValues?.display || ''}
          onClose={handleCloseLocationModal}
          onSave={handleSaveLocation}
        />
      ) : null}
    </article>
  );
}

BandPage.propTypes = {
  band: PropTypes.object,
  onBack: PropTypes.func,
  onOpenMyBand: PropTypes.func,
  backButtonPlacement: PropTypes.oneOf(['bar', 'tabbar']),
  onFollow: PropTypes.func,
  onLeaveBand: PropTypes.func,
  onTickets: PropTypes.func,
  onShare: PropTypes.func,
  onPublicView: PropTypes.func,
  onVisibilityChange: PropTypes.func,
  isVisibilityUpdating: PropTypes.bool,
  onMemberClick: PropTypes.func,
  onFollowerClick: PropTypes.func,
  onPhotoClick: PropTypes.func,
  onEditCover: PropTypes.func,
  onEditAvatar: PropTypes.func,
  onUploadTrack: PropTypes.func,
  onUploadPhoto: PropTypes.func,
  onDeleteTrack: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onMoveTrack: PropTypes.func,
  onReorderPhotos: PropTypes.func,
  onPlayTrack: PropTypes.func,
  onSeekTrack: PropTypes.func,
  onStartPost: PropTypes.func,
  onCreateEvent: PropTypes.func,
  onEditEvent: PropTypes.func,
  onDeleteEvent: PropTypes.func,
  onSubscribe: PropTypes.func,
  canEdit: PropTypes.bool,
  currentUserId: PropTypes.string,
  coverImageUrl: PropTypes.string,
  avatarImageUrl: PropTypes.string,
  posts: PropTypes.array,
  tracks: PropTypes.array,
  playsCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  playingTrackId: PropTypes.string,
  trackProgressById: PropTypes.object,
  deletingTrackId: PropTypes.string,
  isTrackReordering: PropTypes.bool,
  galleryPhotos: PropTypes.array,
  followers: PropTypes.array,
  followersLoaded: PropTypes.bool,
  loadFollowersPage: PropTypes.func,
  deletingGalleryPhotoId: PropTypes.string,
  isGalleryPhotoReordering: PropTypes.bool,
  postComposerProps: PropTypes.object,
  onSaveField: PropTypes.func,
  onSaveLocation: PropTypes.func,
  onSaveMemberInstrument: PropTypes.func,
  onRemoveMember: PropTypes.func,
  removingMemberUserId: PropTypes.string,
  onInviteMember: PropTypes.func,
  isFollowing: PropTypes.bool,
  isFollowLoading: PropTypes.bool,
  isLeaveBandLoading: PropTypes.bool,
  className: PropTypes.string,
};
